import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`, {}, { withCredentials: true });
      setAlerts(alerts.map((a) => (a.id === alertId ? { ...a, is_read: true } : a)));
      toast.success('Alert marked as read');
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const unreadAlerts = alerts.filter((a) => !a.is_read);
  const readAlerts = alerts.filter((a) => a.is_read);

  return (
    <div data-testid="alerts-page" className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Alerts</h1>
        <p className="text-muted-foreground mt-1">
          Budget threshold notifications
        </p>
      </div>

      {/* Unread Alerts */}
      <Card data-testid="unread-alerts-card" className="border-border">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">Unread Alerts</CardTitle>
          <CardDescription>{unreadAlerts.length} notifications</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {unreadAlerts.length === 0 ? (
            <div className="text-center py-8">
              <Check className="h-12 w-12 text-success mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">No unread alerts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unreadAlerts.map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`unread-alert-${alert.id}`}
                  className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg border border-border"
                >
                  <AlertTriangle
                    className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      alert.alert_type === 'exceeded_100'
                        ? 'text-destructive'
                        : 'text-warning'
                    }`}
                    strokeWidth={1.5}
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{alert.budget_head}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    data-testid={`mark-read-${alert.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => markAsRead(alert.id)}
                  >
                    Mark as Read
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Read Alerts */}
      {readAlerts.length > 0 && (
        <Card data-testid="read-alerts-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading">Read Alerts</CardTitle>
            <CardDescription>{readAlerts.length} notifications</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {readAlerts.map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`read-alert-${alert.id}`}
                  className="flex items-start gap-4 p-4 bg-muted/10 rounded-lg border border-border opacity-60"
                >
                  <AlertTriangle
                    className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      alert.alert_type === 'exceeded_100'
                        ? 'text-destructive'
                        : 'text-warning'
                    }`}
                    strokeWidth={1.5}
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{alert.budget_head}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Alerts;
